import React, { forwardRef, useImperativeHandle, useRef } from "react";
import styles from "../styles/search-results.module.css";
import categoryStyles from "../styles/category-tile.module.css";
import t from "../texts/t";
import Link from "next/link";

const SearchResults = forwardRef<TSearchResultsRef, TSearchResultsProps>(
  function SearchResults(
    { visible = true, className, title, results, language, onClose },
    ref
  ) {
    const linksContainerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        next: () => {
          if (!linksContainerRef.current?.children.length) return false;

          const nextElement =
            !document.activeElement ||
            document.activeElement.parentElement !== linksContainerRef.current
              ? linksContainerRef.current.children[0]
              : document.activeElement.nextElementSibling;

          if (nextElement?.tagName === "A") {
            (nextElement as HTMLAnchorElement).focus();
            return true;
          } else {
            return false;
          }
        },
        prev: () => {
          if (!linksContainerRef.current?.children.length) return false;

          const prevElement =
            !document.activeElement ||
            document.activeElement.parentElement !== linksContainerRef.current
              ? linksContainerRef.current.children[
                  linksContainerRef.current.children.length - 1
                ]
              : document.activeElement.previousElementSibling;

          if (prevElement?.tagName === "A") {
            (prevElement as HTMLAnchorElement).focus();
            return true;
          } else {
            return false;
          }
        },
      }),
      []
    );

    return (
      <div
        className={`${className || ""} ${styles.searchSuggestions} ${
          visible
            ? styles.searchSuggestionsVisible
            : styles.searchSuggestionsInvisible
        }`}
      >
        {(title || onClose) && (
          <div className={styles.searchSuggestionsTopLine}>
            {title && (
              <span className={styles.searchSuggestionsTopLineTitle} lang="en">
                {title}
              </span>
            )}
            {onClose && (
              <button
                className={styles.searchSuggestionsTopLineButton}
                onClick={onClose}
                lang={language}
              >
                {t("Close", language)}
              </button>
            )}
          </div>
        )}
        {results.length ? (
          <div
            className={styles.searchSuggestionsList}
            ref={linksContainerRef}
            onClick={onClose}
          >
            {results.map((result) => (
              <Link
                key={result.url}
                className={styles.searchSuggestionsItem}
                href={result.url}
              >
                <figure className={categoryStyles.categoryTileIcon}>
                  {result.icon || "📓"}
                </figure>
                <span>
                  <span className={styles.searchSuggestionsItemTitle}>
                    {result.name}
                  </span>
                  <span className={styles.searchSuggestionsItemSubtitle}>
                    {result.parentCategoryName}
                    {result.parentCategoryName && " ➔ "}
                    {result.categoryName}
                  </span>
                </span>
                <span
                  className={styles.searchSuggestionsItemPublishDate}
                  lang={language}
                >
                  {result.publishDate}
                </span>
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    );
  }
);

export default SearchResults;

export type TSearchResultsRef = {
  next: () => boolean;
  prev: () => boolean;
};

export type TSearchResult = {
  url: string;
  name: string;
  categoryName: string;
  parentCategoryName?: string;
  publishDate: string;
  icon?: string;
};

export type TSearchResultsProps = {
  visible?: boolean;
  className?: string;
  title?: string;
  results: TSearchResult[];
  language?: string;
  onClose?: () => unknown;
};
